import React, { createContext, type PropsWithChildren, useCallback, useContext, useMemo, useState } from "react";

import type { MediaItem } from "@/domain/threadEvent";
import { MessagePhotosModal } from "@/components/photos/MessagePhotosModal";

const clampIndex = (idx: number, length: number): number => {
  if (length <= 0) return 0;
  if (idx < 0) return 0;
  if (idx >= length) return length - 1;
  return idx;
};

type PhotoViewerOpenOptions = {
  initialView?: "grid" | "viewer";
  canRemove?: boolean;
  onRemove?: (photo: MediaItem, index: number) => void;
};

type PhotoViewerApi = {
  open: (photos: readonly MediaItem[], initialIndex: number, options?: PhotoViewerOpenOptions) => void;
};

const PhotoViewerContext = createContext<PhotoViewerApi | null>(null);

export const PhotoViewerProvider = ({ children }: PropsWithChildren) => {
  const [open, setOpen] = useState(false);
  const [photos, setPhotos] = useState<readonly MediaItem[]>([]);
  const [initialIndex, setInitialIndex] = useState(0);
  const [options, setOptions] = useState<PhotoViewerOpenOptions>({});

  const openViewer = useCallback((nextPhotos: readonly MediaItem[], idx: number, nextOptions?: PhotoViewerOpenOptions) => {
    setPhotos(nextPhotos);
    setInitialIndex(idx);
    setOptions(nextOptions || {});
    setOpen(true);
  }, []);

  const api = useMemo<PhotoViewerApi>(() => ({ open: openViewer }), [openViewer]);

  return (
    <PhotoViewerContext.Provider value={api}>
      {children}
      <MessagePhotosModal
        open={open}
        onOpenChange={setOpen}
        photos={photos}
        initialIndex={initialIndex}
        initialView={options.initialView ?? "grid"}
        canRemove={!!options.canRemove}
        onRemove={
          options.onRemove
            ? (photo, index) => {
                options.onRemove?.(photo, index);
                setPhotos((current) => {
                  const next = current.filter((_, idx) => idx !== index);
                  if (!next.length) {
                    setOpen(false);
                    return next;
                  }
                  setInitialIndex((prev) => clampIndex(prev, next.length));
                  return next;
                });
              }
            : undefined
        }
      />
    </PhotoViewerContext.Provider>
  );
};

export const usePhotoViewer = (): PhotoViewerApi => {
  const ctx = useContext(PhotoViewerContext);
  if (!ctx) throw new Error("usePhotoViewer must be used within PhotoViewerProvider");
  return ctx;
};
